// Export the drawing canvas to a size-capped WebP blob (PNG fallback).

import { DRAWING_BACKGROUND_HEX } from '@airhead-games/shared/configs';

// Surface color for the drawing board, shared by the on-screen canvas, the
// export, and the backend rasterizer so every render matches what the player
// drew on.
export const CANVAS_BACKGROUND_COLOR = DRAWING_BACKGROUND_HEX;

// 700 KB decoded keeps the future base64 JSON upload under the backend's 1 MB body parser limit
const MAX_EXPORT_BYTES = 700 * 1024;
const WEBP_QUALITY_STEPS = [0.95, 0.9, 0.8, 0.7];

function toBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality?: number
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

export async function exportDrawing(source: HTMLCanvasElement): Promise<Blob> {
  // The live canvas surface is CSS-only, so the source bitmap is transparent;
  // composite onto the board color so undrawn areas export with the background
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = source.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Could not get 2D context for drawing export');
  }
  ctx.fillStyle = CANVAS_BACKGROUND_COLOR;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0);

  let smallest: Blob | null = null;
  for (const quality of WEBP_QUALITY_STEPS) {
    const blob = await toBlob(canvas, 'image/webp', quality);
    // Browsers without WebP encoding return null or a PNG-typed blob
    if (!blob || blob.type !== 'image/webp') {
      break;
    }
    if (blob.size <= MAX_EXPORT_BYTES) {
      return blob;
    }
    smallest = blob;
  }
  if (smallest) {
    return smallest;
  }

  const png = await toBlob(canvas, 'image/png');
  if (!png) {
    throw new Error('Canvas export produced no image data');
  }
  return png;
}
